import React from 'react';
import { motion } from 'framer-motion';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
const EXAMPLES = [{
  brand: 'Chronic Hand Eczema',
  job: 'Therapy education',
  line: 'One therapy story told as five interactive chapters.',
  image: 'https://cdn.magicpatterns.com/uploads/g441QP1MZjEcErxCEKQcyB/Screenshot_2026-09-21_at_12.15.02.png',
  secondImage: 'https://cdn.magicpatterns.com/uploads/fWSa9teCeuPkJR3piSgWTF/Screenshot_2026-09-21_at_12.15.25.png',
  fit: 'contain' as const
}, {
  brand: 'Adtralza / TraloSpeak',
  job: 'Webinars → evergreen episodes',
  line: 'One evening of webinar became a replayable episode series.',
  image: 'https://cdn.magicpatterns.com/uploads/cGva4EFWoHU27SiQZqBsiD/Screenshot_2026-09-21_at_11.37.44.png',
  fit: 'contain' as const
}, {
  brand: 'Enstilar',
  job: 'Dosing information',
  line: 'Application and titration guidance explored in under a minute.',
  image: 'https://cdn.magicpatterns.com/uploads/4etHrkKD9g6VcK3o1Mgko8/Screenshot_2026-09-21_at_12.47.54.png',
  fit: 'contain' as const
}, {
  brand: 'DERMAWorld',
  job: 'Website engagement',
  line: 'Existing traffic given something to do, not just something to read.',
  image: 'https://cdn.magicpatterns.com/uploads/79sp6FABzXkDguMV2N7JNb/Screenshot_2026-09-21_at_12.58.58.png',
  fit: 'contain' as const
}, {
  brand: 'Patient Guides — Spain',
  job: 'Patient engagement',
  line: 'Approved HCP material adapted into a guided patient experience.',
  image: 'https://cdn.magicpatterns.com/uploads/sh3sX9TJ2njcweNJM26sc3/Screenshot_2026-09-21_at_12.09.33.png',
  fit: 'contain' as const
}, {
  brand: 'Fucidin / Protopic',
  job: 'Markets without local reps',
  line: 'Brands kept present where there is no local sales force.',
  image: 'https://cdn.magicpatterns.com/uploads/3RXaqNv2diTe8CJUBwJmwL/Screenshot_2026-09-21_at_11.52.50.png',
  fit: 'contain' as const
}] as const;
export function SlideAlreadyAtLeo({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner eyebrow="How it happened" data-id="element-50" data-mp="2kzw8bpyl7gwi">
          Snakker did not arrive through one central programme. Brand teams
          found useful applications themselves.
        </SlideBanner>} data-id="element-49" data-mp="2g1kxpd0gx2d8">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex flex-shrink-0 items-end justify-between gap-8" data-id="element-51" data-mp="2uqqy34b1kymp">
        <div className="min-w-0" data-id="element-52" data-mp="33rypr3j1z17t">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-53" data-mp="33ot1fqi903d5">
            Already happening at LEO
          </p>
          <h2 className="text-[clamp(1.9rem,3.6vw,3.3rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-54" data-mp="19e65qg36rtlk">
            LEO is already <span className="text-[var(--brand)]" data-id="element-55" data-mp="16sgbdoz7cbpi">Snakifying.</span>
          </h2>
        </div>
        <p className="hidden max-w-xs flex-shrink-0 text-right text-[clamp(0.78rem,1.05vw,0.98rem)] font-semibold leading-snug text-[var(--muted)] lg:block" data-id="element-56" data-mp="16u9n3ioo9m4d">
          Different brands. Different markets. Different jobs. Same idea.
        </p>
      </motion.header>

      <div className="mt-[clamp(0.9rem,1.8vw,1.6rem)] grid min-h-0 flex-1 grid-cols-2 gap-[clamp(0.5rem,1vw,0.95rem)] lg:grid-cols-3" data-id="element-57" data-mp="s552xvybh7tm">
        {EXAMPLES.map((example, index) => <motion.article key={example.brand} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16 + index * 0.08,
        duration: 0.45
      }} className="flex min-h-0 overflow-hidden rounded-2xl border border-[var(--fg)]/10 bg-white shadow-sm" data-id="element-58" data-mp="1kt9a6lwjdsyf" data-mpi={index}>
            <div className={`relative flex flex-shrink-0 flex-col items-center justify-center gap-1.5 overflow-hidden ${'fit' in example && example.fit === 'contain' ? 'w-[38%] bg-white p-2' : 'w-[34%] bg-[var(--brand)]/5'}`} data-id="element-59" data-mp="4bmv0aswmosj">
              <img src={example.image} alt={`${example.brand} Snak example`} className={'fit' in example && example.fit === 'contain' ? `min-h-0 w-full rounded-sm object-contain ${'secondImage' in example ? 'max-h-[48%]' : 'max-h-full'}` : 'h-full w-full object-cover object-top'} data-id="element-60" data-mp="1eb66f3kjynyy" />
              {'secondImage' in example && <img src={example.secondImage} alt={`${example.brand} Snak example, second screen`} className="max-h-[48%] min-h-0 w-full rounded-sm object-contain" data-id="element-61" data-mp="21zpebhzowyqi" />}
            </div>
            <div className="flex min-w-0 flex-1 flex-col justify-center p-[clamp(0.7rem,1.2vw,1.15rem)]" data-id="element-62" data-mp="j1st11uy4nid">
              <p className="text-[clamp(0.55rem,0.78vw,0.7rem)] font-bold uppercase tracking-[0.16em] text-[var(--brand)]" data-id="element-63" data-mp="e2ybs4cmn70d">
                {example.job}
              </p>
              <h3 className="mt-1 text-[clamp(0.9rem,1.35vw,1.3rem)] font-extrabold leading-tight tracking-[-0.025em] text-[var(--fg)]" data-id="element-64" data-mp="sxw44x7rzwhg">
                {example.brand}
              </h3>
              <p className="mt-[clamp(0.35rem,0.7vw,0.6rem)] text-[clamp(0.68rem,0.95vw,0.88rem)] leading-snug text-[var(--muted)]" data-id="element-65" data-mp="bkukx26yl8i">
                {example.line}
              </p>
            </div>
          </motion.article>)}
      </div>
    </InteriorSlide>;
}